import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { NetstaffLogo } from "@/components/NetstaffLogo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, MapPin, Calendar, Clock } from "lucide-react";

export default function OfferTokenPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/offers/token/${token}`);
        setData(data);
      } catch {
        setData(false);
      } finally { setLoading(false); }
    })();
  }, [token]);

  const respond = async (action) => {
    try {
      await api.post(`/offers/token/${token}/respond`, { action });
      setResponse(action);
    } catch { setResponse("error"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-brand-navy">Loading offer…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-600">Offer not found.</div>;
  const { request, facility } = data;

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        <Link to="/" className="block mb-6"><NetstaffLogo /></Link>
        <Card>
          <CardContent className="p-8">
            {response === "accept" ? (
              <div className="text-center">
                <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
                <h2 className="mt-4 text-2xl font-bold text-brand-navy">Shift confirmed</h2>
                <p className="mt-2 text-slate-600">You're booked. Check your NetStaff app for details.</p>
              </div>
            ) : response === "decline" ? (
              <div className="text-center">
                <XCircle className="w-16 h-16 text-slate-500 mx-auto" />
                <h2 className="mt-4 text-2xl font-bold text-slate-700">Offer declined</h2>
                <p className="mt-2 text-slate-600">Thanks for letting us know.</p>
              </div>
            ) : (
              <>
                <div className="text-xs uppercase tracking-wider text-brand-teal font-semibold">Shift offer</div>
                <h2 className="mt-2 text-2xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {request?.position}
                </h2>
                <div className="mt-4 space-y-2 text-sm text-slate-700">
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-teal" /> {facility?.name} — {facility?.city}</div>
                  <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-brand-teal" /> {request?.shift_date}</div>
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-teal" /> {request?.start_time} – {request?.end_time} ({request?.total_hours}h, {request?.shift_type})</div>
                </div>
                {request?.notes && <p className="mt-4 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{request.notes}</p>}
                <div className="mt-8 grid grid-cols-2 gap-3">
                  <Button variant="outline" onClick={() => respond("decline")} data-testid="offer-decline-btn">Decline</Button>
                  <Button className="bg-brand-teal hover:bg-brand-teal/90" onClick={() => respond("accept")} data-testid="offer-accept-btn">Accept shift</Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
